<script lang="ts">
  /**
   * Custom edge для AutomationEditor — суцільна bezier-крива. При наведенні
   * на зв'язок зʼявляється кнопка ✕ у центрі (видалити з'єднання). Hover
   * відстежуємо через широкий прозорий path (EdgeLabel порталиться окремо,
   * тож CSS :hover з .svelte-flow__edge до кнопки не дотягується).
   */
  import { BaseEdge, EdgeLabel, getBezierPath, useSvelteFlow, Position, type EdgeProps } from '@xyflow/svelte';
  import Icon from '../Icon.svelte';

  let {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style = undefined,
    markerEnd = undefined,
  }: EdgeProps = $props();

  /** Видаляємо ребро через SvelteFlow API — надійно з першого разу (без розсинхрону bind:edges). */
  const { deleteElements } = useSvelteFlow();

  let hovered = $state(false);

  const path = $derived(
    getBezierPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    }),
  );
  const [edgePath, labelX, labelY] = $derived(path);
  /** Стрілка напрямку — рівно над хендлом цілі, спрямована в нього
   *  (кут визначає сторона входу цілі, а не кривина зв'язку). */
  const arrow = $derived.by(() => {
    const offset = 3; // впритул до хендла цілі (кінчик стрілки на доті)
    switch (targetPosition) {
      case Position.Bottom: return { x: targetX, y: targetY + offset, angle: -90 }; // вгору
      case Position.Left:   return { x: targetX - offset, y: targetY, angle: 0 };   // праворуч
      case Position.Right:  return { x: targetX + offset, y: targetY, angle: 180 }; // ліворуч
      case Position.Top:
      default:              return { x: targetX, y: targetY - offset, angle: 90 };  // вниз
    }
  });
</script>

<BaseEdge {id} path={edgePath} {style} {markerEnd} interactionWidth={0} />

<!-- Широка прозора зона для hover уздовж усього зв'язку -->
<path
  d={edgePath}
  fill="none"
  stroke="transparent"
  stroke-width="26"
  style="pointer-events: stroke; cursor: pointer;"
  role="presentation"
  onmouseenter={() => (hovered = true)}
  onmouseleave={() => (hovered = false)}
/>

<!-- Стрілка напрямку — біля ноди-цілі -->
<path
  d="M -5 -4.5 L 4 0 L -5 4.5 Z"
  fill="#7b4fbe"
  transform="translate({arrow.x} {arrow.y}) rotate({arrow.angle})"
  style="pointer-events: none;"
/>

<!-- ✕ зʼявляється ЛИШЕ при наведенні — інакше зв'язок суцільний (без розриву) -->
{#if hovered}
  <EdgeLabel x={labelX} y={labelY}>
    <button
      type="button"
      class="grid place-items-center w-5 h-5 rounded-full bg-white border border-rose-300 text-rose-600 shadow-sm cursor-pointer pointer-events-auto"
      style="z-index: 10;"
      onmouseenter={() => (hovered = true)}
      onmouseleave={() => (hovered = false)}
      onclick={(e) => {
        e.stopPropagation();
        void deleteElements({ edges: [{ id }] });
      }}
      title="Видалити з'єднання"
      aria-label="Видалити з'єднання"
    ><Icon name="close" size={11} /></button>
  </EdgeLabel>
{/if}
